/**
 * Developed by Ashvek Technologies · https://ashvek.com · Evidence over prompts
 */
import * as React from "react";
import { ThreeCanvas } from "./components/ThreeCanvas";
import { TechWizard } from "./components/TechWizard";
import { motionController } from "./lib/gsap_motion";
import { analyticsReporter } from "./lib/analytics";

export function App() {
  const heroRef = React.useRef<HTMLDivElement | null>(null);
  const terminalRef = React.useRef<HTMLDivElement | null>(null);
  const [telemetryReport, setTelemetryReport] = React.useState<string | null>(null);

  React.useEffect(() => {
    analyticsReporter.recordPageview(typeof window !== "undefined" ? window.location.pathname : "/", "Ashvek Technologies Landing Page");
    motionController.init();
    motionController.animateHeroEntrance(heroRef.current);
    motionController.animateTelemetryTerminal(terminalRef.current);
  }, []);

  return (
    <div className="min-h-screen bg-[#090D16] text-[#F8FAFC] flex flex-col font-sans selection:bg-[#FF3B30] selection:text-white">
      {/* 1. Header Navigation */}
      <header className="border-b border-[#1E293B] px-6 py-4 flex items-center justify-between sticky top-0 bg-[#090D16]/90 backdrop-blur-md z-50">
        <div className="flex items-center space-x-3">
          <svg viewBox="0 0 32 32" className="w-6 h-6 stroke-[#FF3B30] fill-none stroke-[2.5]">
            <polygon points="16,4 4,28 28,28" strokeLinejoin="round" />
            <line x1="10" y1="20" x2="22" y2="20" stroke="#FF3B30" strokeWidth="2.5" />
          </svg>
          <span className="font-bold tracking-tight text-sm text-[#F8FAFC]">ASHVEK</span>
          <span className="text-[10px] font-mono text-[#94A3B8] tracking-widest uppercase">TECHNOLOGIES</span>
        </div>
        <nav className="hidden md:flex items-center space-x-6 text-xs text-[#94A3B8]">
          <a href="#proof" className="hover:text-[#F8FAFC] transition">Working Proof</a>
          <a href="#workflow" className="hover:text-[#F8FAFC] transition">How It Works</a>
          <a href="#legacy" className="hover:text-[#F8FAFC] transition">Legacy Modernization</a>
          <a href="#wizard" className="hover:text-[#F8FAFC] transition">Architecture Wizard</a>
          <a href="#trust" className="hover:text-[#F8FAFC] transition">Trust & Limits</a>
        </nav>
        <div className="flex items-center space-x-3">
          <a href="#wizard" className="text-xs font-semibold px-3.5 py-1.5 rounded-lg bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white transition">
            Explore Orbit Build ↗
          </a>
        </div>
      </header>

      {/* 2. Hero Section */}
      <section ref={heroRef} className="relative px-6 py-16 md:py-24 max-w-6xl mx-auto flex flex-col items-center text-center space-y-6">
        <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full border border-[#1E293B] bg-[#111827]/60 text-[11px] font-mono text-[#FF9500]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] animate-pulse" />
          <span>EVIDENCE OVER PROMPTS · GA RELEASE FROZEN</span>
        </div>
        <h1 className="text-4xl sm:text-6xl md:text-7xl font-extrabold tracking-tight text-[#F8FAFC] max-w-4xl leading-tight">
          Build software that <span className="text-[#FF3B30]">doesn't break.</span>
        </h1>
        <p className="text-base sm:text-lg text-[#94A3B8] max-w-2xl font-normal leading-relaxed">
          From customer requirements to tested, verifiable software. No generic AI hallucinations. Every build comes with reproducible tests and cryptographic CAS receipts.
        </p>

        {/* 3D Three.js Signature Canvas */}
        <div className="w-full max-w-2xl pt-4">
          <ThreeCanvas />
        </div>
      </section>

      {/* 3. Working Proof: Real Benchmark Telemetry */}
      <section id="proof" className="px-6 py-16 border-t border-[#1E293B] bg-[#0B101B]/50">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[11px] font-mono text-[#FF3B30] uppercase tracking-wider">Working Proof</span>
              <h2 className="text-2xl font-bold mt-1 text-[#F8FAFC]">Autonomous Synthesis & Auto-Repair Trace</h2>
            </div>
            <span className="text-xs font-mono px-3 py-1 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
              106/106 TESTS PASSING
            </span>
          </div>

          <div ref={terminalRef} className="border border-[#1E293B] rounded-xl bg-[#090D16] p-5 font-mono text-xs space-y-2 text-[#94A3B8]">
            <p className="text-[#94A3B8]/60">$ vyom orbit-build --workload saas --auto-heal</p>
            <p className="text-[#F8FAFC]">✔ Stage 1: Specification synthesized · Digest: sha256:7f9e8a...</p>
            <p className="text-[#F8FAFC]">✔ Stage 2: AIR Plan generated · 9 execution steps verified</p>
            <p className="text-[#F8FAFC]">✔ Stage 3: Materialized 17 files across Three.js, GSAP, React 19, TypeScript, Analytics, SEO</p>
            <p className="text-[#F8FAFC]">✔ Stage 4: Native Telemetry Active · Zero PII Leak · DPDP & GDPR Compliant · DNT Respected</p>
            <p className="text-[#FF9500]">⚠ Injected Defect: SyntaxError detected at line 42</p>
            <p className="text-emerald-400">✔ Autonomous AST Repair: Diagnosed and patched in 0.58s · Verification passes</p>
            <p className="text-emerald-400">✔ Live HTTP instance probed on /healthz: 200 OK (latency: 16ms)</p>
            <p className="text-[#F8FAFC]">✔ Signed delivery manifest: cas://sha256/04c22990...</p>
          </div>
        </div>
      </section>

      {/* 4. How It Works */}
      <section id="workflow" className="px-6 py-16 border-t border-[#1E293B]">
        <div className="max-w-5xl mx-auto space-y-10">
          <div className="text-center space-y-2">
            <span className="text-[11px] font-mono text-[#FF3B30] uppercase tracking-wider">Engineering Lifecycle</span>
            <h2 className="text-3xl font-bold text-[#F8FAFC]">Specify → Configure → Build → Verify</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="p-5 rounded-xl border border-[#1E293B] bg-[#111827]/40 space-y-2">
              <span className="text-[10px] font-mono text-[#FF9500]">01. SPECIFY</span>
              <h3 className="font-semibold text-sm text-[#F8FAFC]">Precise Requirements</h3>
              <p className="text-xs text-[#94A3B8]">Transforms product brief into verified schemas, screen definitions, and security invariants.</p>
            </div>
            <div className="p-5 rounded-xl border border-[#1E293B] bg-[#111827]/40 space-y-2">
              <span className="text-[10px] font-mono text-[#FF9500]">02. CONFIGURE</span>
              <h3 className="font-semibold text-sm text-[#F8FAFC]">Architecture Wizard</h3>
              <p className="text-xs text-[#94A3B8]">Select language, database, and deployment model. Bounded to software development & web sites only.</p>
            </div>
            <div className="p-5 rounded-xl border border-[#1E293B] bg-[#111827]/40 space-y-2">
              <span className="text-[10px] font-mono text-[#FF9500]">03. BUILD & REPAIR</span>
              <h3 className="font-semibold text-sm text-[#F8FAFC]">Autonomous Synthesis</h3>
              <p className="text-xs text-[#94A3B8]">Compiles components, wireframes, and test harness. Automatically diagnoses and repairs compiler errors.</p>
            </div>
            <div className="p-5 rounded-xl border border-[#1E293B] bg-[#111827]/40 space-y-2">
              <span className="text-[10px] font-mono text-[#FF9500]">04. VERIFY & EXPORT</span>
              <h3 className="font-semibold text-sm text-[#F8FAFC]">Signed Delivery</h3>
              <p className="text-xs text-[#94A3B8]">Live health probe check, downloadable ZIP, or direct push to your GitHub repo with Ed25519 signatures.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Legacy Software Modernization Section */}
      <section id="legacy" className="px-6 py-16 border-t border-[#1E293B] bg-[#090D16]">
        <div className="max-w-5xl mx-auto space-y-10">
          <div className="text-center space-y-2">
            <span className="text-[11px] font-mono text-[#FF3B30] uppercase tracking-wider">Enterprise Invariants</span>
            <h2 className="text-3xl font-bold text-[#F8FAFC]">Legacy Software Support & Modernization</h2>
            <p className="text-sm text-[#94A3B8] max-w-2xl mx-auto">
              Wholesale rewrites fail. Ashvek treats legacy systems with the Strangler Fig pattern, locking in baseline invariants with characterization tests before modernizing code.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="p-5 rounded-xl border border-[#1E293B] bg-[#111827]/40 space-y-2">
              <span className="text-[10px] font-mono text-[#FF9500]">PILLAR 01</span>
              <h3 className="font-semibold text-sm text-[#F8FAFC]">Ingest & Map</h3>
              <p className="text-xs text-[#94A3B8]">Extracts AST call graphs, database schemas, and dead code boundaries without human speculation.</p>
            </div>
            <div className="p-5 rounded-xl border border-[#1E293B] bg-[#111827]/40 space-y-2">
              <span className="text-[10px] font-mono text-[#FF9500]">PILLAR 02</span>
              <h3 className="font-semibold text-sm text-[#F8FAFC]">Characterization Lock</h3>
              <p className="text-xs text-[#94A3B8]">Captures golden input/output pairs to freeze existing system behavior before touching a single line of code.</p>
            </div>
            <div className="p-5 rounded-xl border border-[#1E293B] bg-[#111827]/40 space-y-2">
              <span className="text-[10px] font-mono text-[#FF9500]">PILLAR 03</span>
              <h3 className="font-semibold text-sm text-[#F8FAFC]">Autonomous AST Repair</h3>
              <p className="text-xs text-[#94A3B8]">Synthesizes surgical, single-purpose diffs and auto-repairs compiler breakages until all characterization tests pass.</p>
            </div>
            <div className="p-5 rounded-xl border border-[#1E293B] bg-[#111827]/40 space-y-2">
              <span className="text-[10px] font-mono text-[#FF9500]">PILLAR 04</span>
              <h3 className="font-semibold text-sm text-[#F8FAFC]">Canary & Rollback</h3>
              <p className="text-xs text-[#94A3B8]">Deploys via blue/green staging probes with instant automated rollback if latency or error budgets breach thresholds.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Interactive Architecture & Modernization Wizard */}
      <section id="wizard" className="px-6 py-16 border-t border-[#1E293B] bg-[#0B101B]/40">
        <TechWizard />
      </section>

      {/* 7. Trust & Honest Limitations */}
      <section id="trust" className="px-6 py-16 border-t border-[#1E293B]">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="space-y-2">
            <span className="text-[11px] font-mono text-[#FF3B30] uppercase tracking-wider">Governed Policy</span>
            <h2 className="text-2xl font-bold text-[#F8FAFC]">Trust, Security Boundaries & Limitations</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs text-[#94A3B8]">
            <div className="p-5 rounded-xl border border-[#1E293B] bg-[#111827]/30 space-y-2">
              <h4 className="font-semibold text-[#F8FAFC] text-sm">Strict ₹0 Paid Marketing Policy</h4>
              <p>Ashvek Technologies commits ₹0 to paid advertising, sponsored placements, or synthetic marketing. Customer acquisition is grounded solely in reproducible software proof.</p>
            </div>
            <div className="p-5 rounded-xl border border-[#1E293B] bg-[#111827]/30 space-y-2">
              <h4 className="font-semibold text-[#F8FAFC] text-sm">No Synthetic Testimonials</h4>
              <p>We do not fabricate customer discovery quotes or fictitious logos. All pilot evaluations require confirmed engineering leads and passing automated test suites.</p>
            </div>
            <div className="p-5 rounded-xl border border-[#1E293B] bg-[#111827]/30 space-y-2">
              <h4 className="font-semibold text-[#F8FAFC] text-sm">Target Scope Restriction</h4>
              <p>Orbit Build synthesizes software development and web site projects only. Generative AI spam, blog text generators, and generic marketing copy are explicitly excluded.</p>
            </div>
            <div className="p-5 rounded-xl border border-[#1E293B] bg-[#111827]/30 space-y-2">
              <h4 className="font-semibold text-[#F8FAFC] text-sm">Verifiable Offline Execution</h4>
              <p>Every delivered ZIP contains standard unit and E2E tests that pass locally with zero network dependency on Ashvek cloud servers.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Footer */}
      <footer className="border-t border-[#1E293B] px-6 py-10 mt-auto bg-[#090D16] text-xs text-[#94A3B8] flex flex-col sm:flex-row items-center justify-between gap-4">
        <div>
          <p className="font-bold text-[#F8FAFC]">ASHVEK TECHNOLOGIES</p>
          <p className="text-[11px] text-[#94A3B8]/60 mt-1">Proprietor: Tarak B Patel · <a href="https://ashvek.com" className="text-[#FF3B30] hover:underline">https://ashvek.com</a> · Chennai, India</p>
        </div>
        <div className="text-[11px] font-mono flex items-center space-x-4">
          <button
            onClick={() => setTelemetryReport(analyticsReporter.exportReportJSON())}
            className="text-[#FF3B30] hover:underline cursor-pointer"
          >
            Inspect Local Telemetry Report
          </button>
          <span>RELEASE BUNDLE: FROZEN · VYOM CORE v1.0.0-GA</span>
        </div>
      </footer>

      {/* 9. Local Telemetry Report Modal */}
      {telemetryReport && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#090D16] border border-[#1E293B] rounded-2xl max-w-2xl w-full p-6 space-y-4 font-mono text-xs shadow-2xl">
            <div className="flex justify-between items-center border-b border-[#1E293B] pb-3">
              <span className="font-bold text-[#F8FAFC]">VERIFIABLE LOCAL TELEMETRY REPORT</span>
              <button onClick={() => setTelemetryReport(null)} className="text-[#94A3B8] hover:text-[#F8FAFC]">✕</button>
            </div>
            <pre className="p-4 rounded-xl bg-[#111827] text-emerald-400 overflow-x-auto max-h-96 text-[11px]">
              {telemetryReport}
            </pre>
            <div className="text-right">
              <button onClick={() => setTelemetryReport(null)} className="px-4 py-2 rounded-lg bg-[#FF3B30] text-white font-semibold">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
